/**
 * CSS variable resolution utilities, kept in a separate module so that
 * Web Worker entry points can import `seedResolvedVariables` without pulling
 * in utils.ts (which imports styled-components and triggers Vite's CSS HMR
 * machinery — updateStyle() — at module-load time, crashing because `document`
 * is not defined in worker scope).
 *
 * Both this module and utils.ts (which re-exports from here) share the same
 * module instance in each JavaScript realm (main thread / worker), so seeding
 * done here is visible to every caller of resolveCssVariable regardless of
 * which import path they used.
 */
const resolvedVariablesCache = new Map();
let computedStyles;
/**
 * Pre-populate the resolved-variable cache with values obtained on the main
 * thread. Call this in Web Worker contexts (where `document` is unavailable)
 * BEFORE any module that calls resolveCssVariable at initialization time is
 * imported, so those module-level calls hit the cache instead of touching DOM.
 */
export function seedResolvedVariables(variables) {
    for (const [key, value] of Object.entries(variables)) {
        resolvedVariablesCache.set(key, value);
    }
}
export function resolveCssVariable(cssVariable) {
    // Check cache first — allows worker contexts to pre-seed via seedResolvedVariables().
    const cached = resolvedVariablesCache.get(cssVariable);
    if (cached !== undefined)
        return cached;
    // In non-browser environments (e.g. Web Workers that haven't been seeded),
    // return the raw variable string rather than crashing.
    if (typeof document === 'undefined') {
        return cssVariable;
    }
    if (!computedStyles) {
        computedStyles = getComputedStyle(document.documentElement);
    }
    let value = resolvedVariablesCache.get(cssVariable);
    if (!value) {
        const cssVarName = cssVariable.match(/^var\((.*)\)$/)?.[1].trim() ?? '';
        value = computedStyles.getPropertyValue(cssVarName).trim();
        // Expand 3-letter hex shorthand (#fff, #000) to 6-letter format
        const shortHex = value.match(/^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/);
        if (shortHex) {
            value = `#${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}${shortHex[3]}${shortHex[3]}`;
        }
        resolvedVariablesCache.set(cssVariable, value);
    }
    return value;
}
